// Shared point-by-point reconstruction used by both PowerPlayReport.jsx and
// MomentumChart.jsx — a rally's serving team follows the same convention used
// throughout the app for parsed DVW events (EventItem.jsx/VideoAnalysis.jsx):
// the scout code's team prefix ("*" = home), falling back to `homePlayer` for
// DVMatchCoding-coded events that have no raw dvString.
import { calculateAllStats, createStatsItem, doEvent } from "./StatsItem";
import { isHomeTeamEvent } from "./EventUtils";

export { isHomeTeamEvent } from "./EventUtils";

const kSkillServe = 1;
const kSkillPass = 2;
const kSkillSpike = 4;
const kSkillBlock = 5;
const kSkillDefense = 6;
// Only these skills can actually end a rally outright on a grade-3 "positive"
// outcome (ace / kill / stuff block) — a perfect reception or dig (grade 3)
// is still in-play and can never be the rally's key event, even if it
// happens to be the last grade-3 event recorded before a later error.
const kTerminalPositiveSkills = [kSkillServe, kSkillSpike, kSkillBlock];

// Same red/yellow/green convention as DVMate iOS's BeachMomentumChartView
// colorByGrade: EventGrade 0 = error, 3 = kill/ace, 1-2 = still in play.
export function colourByGrade(grade) {
  if (grade === 0) return "#e74c3c";
  if (grade === 3) return "#2ecc71";
  return "#f39c12";
}

// Builds one row per completed point: the score right after that rally, who
// served it, the running differential, and whether it was played under a
// Power Play (taken from the serve event that started the rally —
// DVWFile.js's markPowerPlayRallies tags every event of a rally with the
// code active as of its own serve). DataVolley records the PRE-rally score
// on a serve event, so point N's resulting score is read off serve N+1 (or
// the set's final score for the very last point).
export function buildPointByPoint(set) {
  const events = set.events ?? [];
  const serves = events.filter((ev) => ev.EventType === kSkillServe);
  const finalA = set.HomeScore ?? set.pointsA ?? 0;
  const finalB = set.AwayScore ?? set.pointsB ?? 0;

  const points = [];
  let prevA = 0;
  let prevB = 0;
  // Each team's own rotation only changes when THAT team starts a new
  // serving rally (sideout) — carry the last-seen row forward across points
  // where the rally has no event from that team at all.
  let lastRowA = null;
  let lastRowB = null;
  for (let i = 0; i < serves.length; i++) {
    const serve = serves[i];
    const next = serves[i + 1];
    const pointsA = next ? next.TeamScore ?? prevA : finalA;
    const pointsB = next ? next.OppositionScore ?? prevB : finalB;
    const scoringTeam = pointsA > prevA ? "A" : pointsB > prevB ? "B" : null;
    const pointsGained = Math.max(pointsA - prevA, pointsB - prevB, 0);
    const powerPlayTeams = serve.powerPlayTeams ?? [];
    const server = isHomeTeamEvent(serve) ? "A" : "B";
    const receiver = server === "A" ? "B" : "A";
    // The rally's last recorded event (immediately before the next serve, or
    // the set's final event for the last point) — the action that actually
    // ended the point, mirroring DVMate iOS's MatchRally.keyEvent. Prefer a
    // POSITIVE (grade 3 — kill/ace) event if the rally has one, even if a
    // later reactive event (e.g. the opposing team's block/dig error on that
    // same kill) comes after it chronologically — that reactive event isn't
    // what decided the rally.
    const serveIdx = events.indexOf(serve);
    const nextIdx = next ? events.indexOf(next) : events.length;
    const rallyEvents = events.slice(serveIdx, nextIdx);
    const positiveEvents = rallyEvents.filter(
      (ev) => ev.EventGrade === 3 && kTerminalPositiveSkills.includes(ev.EventType)
    );
    const keyEvent =
      positiveEvents.length > 0
        ? positiveEvents[positiveEvents.length - 1]
        : rallyEvents.length > 0
        ? rallyEvents[rallyEvents.length - 1]
        : serve;
    const keyEventTeam = isHomeTeamEvent(keyEvent) ? "A" : "B";
    const receiveEvent = rallyEvents.find((ev) => ev.EventType === kSkillPass) ?? null;
    const sideout = scoringTeam !== null && scoringTeam !== server;
    // Approximates DVMate's sideoutSpikeKill/sideoutOppError (the full
    // first-ball-vs-transition distinction needs deeper rally-phase tracking
    // not reconstructed here): a successful sideout ending on the receiving
    // team's own kill ("kill"), or on the serving team's own error ("oppError").
    let sideoutSubtype = null;
    if (sideout) {
      if (keyEventTeam === receiver && keyEvent.EventType === kSkillSpike && keyEvent.EventGrade === 3) {
        sideoutSubtype = "kill";
      } else if (keyEventTeam === server && keyEvent.EventGrade === 0) {
        sideoutSubtype = "oppError";
      }
    }
    // "First ball" sideout (DVMate's spikeEvents.length==0 check): the
    // receiving team won the rally before making any EARLIER attack attempt
    // of their own — anything else that still ends in a sideout (a dig/
    // counter-attack after a stuffed first attempt) is a "transition" sideout.
    const keyIdx = events.indexOf(keyEvent);
    const earlierReceiverSpikes = rallyEvents.filter(
      (ev) => ev.EventType === kSkillSpike && (isHomeTeamEvent(ev) ? "A" : "B") === receiver && events.indexOf(ev) < keyIdx
    ).length;
    const isFirstBall = sideout && earlierReceiverSpikes === 0;
    for (const ev of rallyEvents) {
      if (isHomeTeamEvent(ev)) lastRowA = ev.Row ?? lastRowA;
      else lastRowB = ev.Row ?? lastRowB;
    }
    points.push({
      index: i + 1,
      server,
      receiver,
      serveEvent: serve,
      receiveEvent,
      keyEvent,
      keyEventTeam,
      sideout,
      sideoutSubtype,
      isFirstBall,
      // Every event belonging to this rally (serve through the event right
      // before the next serve) — lets callers (e.g. computeMatchStats) work
      // out which rally a given raw event belongs to.
      rallyEvents,
      // The score AT the serve (pre-rally) — row 1 of the per-rally column.
      preA: prevA,
      preB: prevB,
      rowA: lastRowA,
      rowB: lastRowB,
      pointsA,
      pointsB,
      scoringTeam,
      powerPlay: serve.powerPlay ?? null,
      powerPlayTeams,
      isBonus: !!(scoringTeam && powerPlayTeams.includes(scoringTeam) && pointsGained >= 2),
    });
    prevA = pointsA;
    prevB = pointsB;
  }
  return points;
}

// Short labels for the key-event marker (DVMate's eventString, simplified —
// that version substitutes "SPIKE"/"SERVE" for synthetic opposition event
// types that don't apply here since VBLive already parses both teams fully).
const kSkillLabels = {
  1: "Serve",
  2: "Reception",
  3: "Set",
  4: "Attack",
  5: "Block",
  6: "Dig",
  7: "Freeball",
};

export function skillLabel(ev) {
  return kSkillLabels[ev?.EventType] ?? "";
}

// Team-level comparison stats for one or more sets, mirroring DVMate iOS's
// BeachStatsView: Sideout %, Break Point %, First Ball Sideout %, Sideout
// Transition %, FB Sideout Kills %, Sideout Opposition Error % (all derived
// from buildPointByPoint's reconstructed rallies), plus Plus/Minus/Blocks/
// Digs/Positive Serve % (via the existing StatsItem.js doEvent/
// calculateAllStats pipeline, applied per-team across the same sets).
// `setEntries` is an array of { setNumber, set } — the true (positional)
// set number of each entry is needed so the returned rally identifiers line
// up with MomentumChart.jsx's own `p.setNumber` tagging, even when only a
// single set's worth of entries is passed in (e.g. one tab of the Momentum
// Chart page's stats panel).
const kMetricKeys = [
  "sideout",
  "breakPoint",
  "firstBallSideout",
  "transition",
  "fbKill",
  "oppError",
  "plus",
  "minus",
  "blocks",
  "digs",
  "positiveServe",
];

function emptyRallyBuckets() {
  const buckets = {};
  for (const key of kMetricKeys) buckets[key] = new Map();
  return buckets;
}

function addRally(buckets, metric, setNumber, index) {
  buckets[metric].set(`${setNumber}-${index}`, { setNumber, index });
}

export function computeMatchStats(setEntries) {
  const statsA = createStatsItem(null);
  const statsB = createStatsItem(null);
  let attemptsA = 0, successA = 0, firstBallA = 0, fbKillA = 0, oppErrorA = 0;
  let attemptsB = 0, successB = 0, firstBallB = 0, fbKillB = 0, oppErrorB = 0;
  let serveTotalA = 0, breakPointsA = 0, serveTotalB = 0, breakPointsB = 0;
  const bucketsA = emptyRallyBuckets();
  const bucketsB = emptyRallyBuckets();

  for (const { setNumber, set } of setEntries) {
    const points = buildPointByPoint(set);
    const eventToPoint = new Map();
    for (const p of points) {
      for (const ev of p.rallyEvents) eventToPoint.set(ev, p);
    }

    for (const ev of set.events ?? []) {
      const side = isHomeTeamEvent(ev) ? "A" : "B";
      doEvent(ev, side === "A" ? statsA : statsB);
      const point = eventToPoint.get(ev);
      if (!point) continue;
      const buckets = side === "A" ? bucketsA : bucketsB;
      const grade = ev.EventGrade;
      const isScoringAttack =
        (ev.EventType === kSkillServe && grade === 3) ||
        (ev.EventType === kSkillSpike && grade === 3) ||
        (ev.EventType === kSkillBlock && grade >= 2);
      if (isScoringAttack) {
        addRally(buckets, "plus", setNumber, point.index);
        if (ev.EventType === kSkillBlock) addRally(buckets, "blocks", setNumber, point.index);
      }
      const isError =
        grade === 0 &&
        (ev.EventType === kSkillServe ||
          ev.EventType === kSkillPass ||
          ev.EventType === kSkillSpike ||
          ev.EventType === kSkillBlock ||
          ev.EventType === kSkillDefense);
      if (isError) addRally(buckets, "minus", setNumber, point.index);
      if (ev.EventType === kSkillDefense && grade >= 1) addRally(buckets, "digs", setNumber, point.index);
      if (ev.EventType === kSkillServe && (grade === 3 || grade === 4)) addRally(buckets, "positiveServe", setNumber, point.index);
    }

    for (const p of points) {
      const buckets = p.receiver === "A" ? bucketsA : bucketsB;
      const serverBuckets = p.server === "A" ? bucketsA : bucketsB;
      if (p.server === "A") {
        serveTotalA++;
        if (!p.sideout) breakPointsA++;
      } else {
        serveTotalB++;
        if (!p.sideout) breakPointsB++;
      }
      if (!p.sideout) {
        addRally(serverBuckets, "breakPoint", setNumber, p.index);
        continue;
      }
      const isA = p.receiver === "A";
      if (isA) successA++;
      else successB++;
      addRally(buckets, "sideout", setNumber, p.index);
      if (p.isFirstBall) {
        if (isA) firstBallA++;
        else firstBallB++;
        addRally(buckets, "firstBallSideout", setNumber, p.index);
        if (p.sideoutSubtype === "kill") {
          if (isA) fbKillA++;
          else fbKillB++;
          addRally(buckets, "fbKill", setNumber, p.index);
        }
      } else {
        addRally(buckets, "transition", setNumber, p.index);
      }
      if (p.sideoutSubtype === "oppError") {
        if (isA) oppErrorA++;
        else oppErrorB++;
        addRally(buckets, "oppError", setNumber, p.index);
      }
    }
    for (const p of points) {
      if (p.receiver === "A") attemptsA++;
      else attemptsB++;
    }
  }
  calculateAllStats(statsA);
  calculateAllStats(statsB);

  const toArray = (buckets) => {
    const out = {};
    for (const key of kMetricKeys) out[key] = Array.from(buckets[key].values());
    return out;
  };

  const pct = (n, d) => (d > 0 ? (n * 100) / d : 0);
  const build = (success, attempts, firstBall, fbKill, oppError, serveTotal, breakPoints, stats, buckets) => ({
    sideoutPercent: pct(success, attempts),
    sideoutSub: `${success}/${attempts}`,
    pointPercent: pct(breakPoints, serveTotal),
    pointSub: `${breakPoints}/${serveTotal}`,
    firstBallSideoutPercent: pct(firstBall, attempts),
    firstBallSub: `${firstBall}/${attempts}`,
    transitionPercent: pct(success - firstBall, attempts),
    transitionSub: `${success - firstBall}/${attempts}`,
    fbKillPercent: pct(fbKill, attempts),
    fbKillSub: `${fbKill}/${attempts}`,
    oppErrorPercent: pct(oppError, attempts),
    oppErrorSub: `${oppError}/${attempts}`,
    plus: stats.plus,
    minus: stats.minus,
    blocks: stats.BlockPoints,
    digs: stats.GoodDigs,
    positiveServePercent: stats.GoodServePercent,
    positiveServeSub: `${stats.GoodServe}/${stats.ServeTotal}`,
    rallies: toArray(buckets),
  });

  return {
    A: build(successA, attemptsA, firstBallA, fbKillA, oppErrorA, serveTotalA, breakPointsA, statsA, bucketsA),
    B: build(successB, attemptsB, firstBallB, fbKillB, oppErrorB, serveTotalB, breakPointsB, statsB, bucketsB),
  };
}
